import { useEffect, useRef, useState } from 'react';
import { Dialog } from './dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { PendingCertificateNameDto } from '../../general/dtos';
import { useApi } from '../../providers/api-provider';
import { useFullscreenDialogParent } from '../../general/use-fullscreen-dialog-parent';

export function CertificateNameDialog({ certificate, onConfirmed, onLater, embedded = false, onBusyChange }: {
    embedded?: boolean; onBusyChange?: (busy: boolean) => void;
    certificate: Pick<PendingCertificateNameDto, 'key' | 'course_name' | 'default_name'>; onConfirmed: () => void; onLater: () => void;
}) {
    const api = useApi();
    const parent = useFullscreenDialogParent();
    const [name, setName] = useState(certificate.default_name);
    const [verified, setVerified] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const pending = useRef(false), mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!api || pending.current || !verified || !name.trim()) return;
        pending.current = true; setBusy(true); onBusyChange?.(true); setError('');
        try {
            await api.confirmCertificateName(certificate.key, name.trim());
            if (mounted.current) onConfirmed();
        } catch (failure: any) {
            if (mounted.current) setError(failure?.response?.data?.message || failure?.message || 'Could not confirm your certificate name. Please try again.');
        } finally { pending.current = false; if (mounted.current) { setBusy(false); onBusyChange?.(false); } }
    };
    const content = <>
        <form onSubmit={save} className="flex flex-column gap-3">
            <p className="m-0">Enter the name to appear on your certificate for <strong>{certificate.course_name}</strong>.</p>
            <label htmlFor="certificate-recipient-name">Name on certificate</label>
            <InputText id="certificate-recipient-name" value={name} maxLength={250} required autoComplete="name" disabled={busy}
                onChange={event => { setName(event.target.value); setVerified(false); setError(''); }} className="w-full" />
            <p className="m-0 p-3 surface-100 border-round">Please make sure your name is specified correctly because you will not be able to change it later.</p>
            <div className="flex align-items-start gap-2">
                <Checkbox inputId="certificate-name-verified" checked={verified} disabled={busy} onChange={event => setVerified(!!event.checked)} />
                <label htmlFor="certificate-name-verified">I have checked and verified my name is spelled correctly</label>
            </div>
            {error && <div role="alert" className="p-3 border-round surface-100 text-red-500">{error}</div>}
            <div className="flex justify-content-end gap-2">
                {!embedded && <Button type="button" label="Later" text disabled={busy} onClick={() => { if (!pending.current) onLater(); }} />}
                <Button type="submit" label="Confirm name" icon="pi pi-check" loading={busy} disabled={!api || busy || !verified || !name.trim()} />
            </div>
        </form>
    </>;
    if (embedded) return content;
    return <Dialog header="Name on your certificate" visible modal appendTo={parent} baseZIndex={11000}
        style={{ width: '34rem', maxWidth: '95vw' }} draggable={false} closable={!busy} closeOnEscape={!busy}
        onHide={() => { if (!pending.current) onLater(); }}>
        {content}
    </Dialog>;
}
